import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import isEqual from 'lodash.isequal';

import Card from '~/shared/components/Card';
import { SelectOption } from '~/shared/utils/types';
import Table from '~/shared/components/Table';
import { useXFetch } from '~/shared/utils/useXFetch';
import { Volume, VolumeCountResponse, VolumeResponse } from '~/shared/types/api';
import Button from '~/shared/components/Buttons';
import CheckIcon from '~/shared/assets/icons/check.svg';
import FilterRemoveIcon from '~/shared/assets/icons/filter-remove.svg';
import PageContainer from '~/shared/components/PageContainer';
import { API_FILTER_KEY } from '~/shared/utils/constants';
import Typography from '~/shared/components/Typography';
import { toaster } from '~/shared/utils/toaster';

import DashboardFilter from '../DashboardFilter';
import DashboardCharts from '../DashboardCharts';
import { refetchIntervalMs } from '../common/helpers';

import { getColumnDefs } from './columnDefs';
import {
  OptimisticSwitchType,
  baseUrl,
  canBeDatafied,
  datafyErrorToast,
  getApiFilterFromSelectOption,
  updateOptimisticVolumeSwitch,
} from './helpers';

const dataLimit = 50;
const sortKey = 'sort_field';

export default function FleetManagerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = JSON.parse(searchParams.get(API_FILTER_KEY) || '[]') as SelectOption[];
  const sortField: { field: string; dir: 'desc' | 'asc' } = JSON.parse(
    searchParams.get(sortKey) as string
  );

  const [selectedVolumes, setSelectedVolumes] = useState<string[]>([]);
  const [pageOffset, setPageOffset] = useState('0');
  const [dataCount, setDataCount] = useState(0);
  const [optimisticVolumeSwitches, setOptimisticVolumeSwitches] = useState<OptimisticSwitchType>(
    []
  );

  const dashboardChartsRef = useRef<{ savingsQueryKey: string; utilizationQueryKey: string }>(null);

  const savingsChartQueryKey = dashboardChartsRef?.current?.savingsQueryKey;
  const utilizationChartQueryKey = dashboardChartsRef?.current?.utilizationQueryKey;

  const api = useXFetch();
  const c = useQueryClient();
  const qs = new URLSearchParams({
    sortBy: JSON.stringify({
      field: sortField?.field || 'volumeId',
      ascending: sortField?.dir !== 'desc',
    }),
    limit: dataLimit.toString(),
    offset: pageOffset,
  });

  const { data, isLoading } = useQuery({
    queryKey: [baseUrl, JSON.stringify({ filters, sortField, pageOffset })],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      filters.forEach((f) => {
        if (!qs.has('filters', getApiFilterFromSelectOption(f))) {
          qs.append('filters', getApiFilterFromSelectOption(f));
        }
      });

      const volumes = await api<VolumeResponse>(`${baseUrl}?${qs.toString()}`);

      const previousStatuses = (data || { volumes: [] })?.volumes.map(
        ({ monitoring, volumeId }) => ({
          monitoring,
          volumeId,
        })
      );

      const currentStatuses = volumes?.volumes.map(({ monitoring, volumeId }) => ({
        monitoring,
        volumeId,
      }));

      if (!isEqual(previousStatuses, currentStatuses)) {
        const changedStatuses = previousStatuses.filter((prev) => {
          const currentStatus = currentStatuses.find((s) => prev.volumeId === s.volumeId);
          return currentStatus && currentStatus.monitoring !== prev.monitoring;
        });

        changedStatuses.forEach((prev) => {
          const changedStatus = currentStatuses.find((curr) => curr.volumeId === prev.volumeId);
          console.log({ prev, changedStatus });

          if (changedStatus) {
            if (changedStatus.monitoring === 'Monitored' && prev.monitoring === 'Migrating') {
              datafyErrorToast(changedStatus.volumeId);
            }

            if (!changedStatus?.monitoring.endsWith('ing')) {
              console.log({ prev, curr: changedStatus });
              c.refetchQueries({ queryKey: [savingsChartQueryKey] });
              c.refetchQueries({ queryKey: [utilizationChartQueryKey] });
            }
          }
        });
      }

      const { count } = await api<VolumeCountResponse>(`${baseUrl}/count?${qs.toString()}`);

      if (count && !isNaN(count)) {
        setDataCount(count);
      }

      return {
        ...volumes,
        ...{ count },
      };
    },

    refetchInterval: refetchIntervalMs,
  });

  const rowData = useMemo(() => data?.volumes, [data?.volumes]);

  const { mutateAsync: bulkDatafy, isPending: isBulkDatafyPending } = useMutation({
    mutationKey: ['bulk-datafy'],
    mutationFn: async (volumes: { volumeId: Volume['volumeId'] }[]) => {
      const res = await api<unknown>(`${baseUrl}/bulk-datafy`, {
        method: 'POST',
        body: JSON.stringify({ volumes }),
      });
      return res;
    },
  });

  const columnDefs = useMemo(
    () =>
      getColumnDefs({
        onSelectAllRows: () =>
          setSelectedVolumes((prev) =>
            prev?.length > 0
              ? []
              : data?.volumes.filter(canBeDatafied).map((v) => v.volumeId!) || []
          ),
        sortField,
        selectedVolumes,
        setSelectedVolumes,
        optimisticVolumeSwitches,
        setOptimisticVolumeSwitches,
      }),
    [selectedVolumes, sortField, optimisticVolumeSwitches, data?.volumes]
  );

  const onPageChange = (page: number): void => {
    setSelectedVolumes([]);
    setPageOffset(`${page * dataLimit}`);
  };

  async function onBulkDatafyClick() {
    const volumes = selectedVolumes.map((v) => ({ volumeId: v }));

    if (volumes)
      try {
        volumes.forEach((v) =>
          setOptimisticVolumeSwitches((prev) =>
            updateOptimisticVolumeSwitch(prev, v.volumeId!, true, Date.now())
          )
        );

        await bulkDatafy(volumes);
        setSelectedVolumes([]);
      } catch {
        toaster({
          variant: 'error',
          title: 'Datafication Error',
          message: `Volumes ${volumes
            .map((volume) => `[${volume.volumeId}]`)
            .join(', ')} have failed to Datafy.`,
        });
        volumes.forEach((v) =>
          setOptimisticVolumeSwitches((prev) => updateOptimisticVolumeSwitch(prev, v.volumeId!))
        );
      }
  }

  return (
    <PageContainer>
      <div className="flex gap-[48px] items-start">
        <DashboardFilter
          filters={filters}
          setFilters={(value: SelectOption[] | ((filter: SelectOption[]) => SelectOption[])) => {
            setPageOffset('0');
            const val = typeof value === 'function' ? value(filters) : value;

            qs.delete('filters');
            setSearchParams((params) => {
              params.set(API_FILTER_KEY, JSON.stringify(val));
              return params;
            });
          }}
        />

        <button
          onClick={() =>
            setSearchParams((params) => {
              params.delete(API_FILTER_KEY);
              return params;
            })
          }
          className="flex gap-[10px] items-center h-[48px]"
        >
          <FilterRemoveIcon className="w-[24px] aspect-square fill-primary-50" />
          <Typography variant="p3" color="text-primary-50">
            Clear selection
          </Typography>
        </button>
      </div>

      <DashboardCharts ref={dashboardChartsRef} />

      <Card className="flex flex-col h-[628px] !pt-[0px]">
        <div className="py-[24px] flex flex-col gap-2">
          <div className="h-[44.5px] flex items-center">
            <Typography variant="H5" className="pr-[16px]">
              Volumes{' '}
              {data?.count !== undefined ? (
                `(${data?.count})`
              ) : (
                <span>
                  ({<ArrowPathIcon className="h-6 w-4 animate-spin  stroke-[2px] inline-block" />})
                </span>
              )}
            </Typography>

            <div
              className={clsx(
                'w-[1px] h-full bg-[#E5EDF4] transition-opacity duration-200',
                selectedVolumes?.length ? 'opacity-1' : 'opacity-0'
              )}
            />
            <div
              className={clsx(
                'flex items-center gap-4 transition-opacity duration-200',
                selectedVolumes?.length ? 'opacity-1' : 'opacity-0'
              )}
            >
              <Typography variant="p1" className="px-[16px]">
                <span className="w-10 text-right inline-block">{selectedVolumes?.length}</span>{' '}
                Volumes Selected
              </Typography>
              <Button
                onClick={onBulkDatafyClick}
                className="min-w-[158.66px]"
                loading={isBulkDatafyPending}
                disabled={!selectedVolumes?.length}
                rounded
              >
                <div className="bg-white rounded-full w-[17.5px] h-[17.5px] flex justify-center items-center z-[1] mr-[10px]">
                  <CheckIcon className="stroke-primary-500 transition-opacity duration-200" />
                </div>
                Datafy Selected
              </Button>

              <Button onClick={() => setSelectedVolumes([])} variant="cancel">
                Clear Selection
              </Button>
            </div>
          </div>
        </div>

        <Table
          rowData={rowData}
          loading={isLoading}
          columnDefs={columnDefs}
          defaultColDef={{ filter: false }}
          pagination={{
            perPage: dataLimit,
            onChange: onPageChange,
            totalPages: Math.ceil(dataCount / dataLimit),
          }}
          headerHeight={80}
          onSortChange={(sort) => {
            setSearchParams((params) => {
              params.set(sortKey, JSON.stringify(sort));
              return params;
            });
          }}
        />
      </Card>
    </PageContainer>
  );
}
